import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Building2, 
  Heart, 
  ShoppingBag, 
  Users, 
  TrendingUp, 
  ArrowRight,
  Wallet,
  Target,
  Sparkles,
  ChevronRight,
  PiggyBank,
  LineChart,
  Gift,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Clock,
  Shield,
  Zap,
  Star,
  ExternalLink
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { API } from '../App';

export default function LandingPage({ auth }) {
  const navigate = useNavigate();
  const { user } = auth;
  const [stats, setStats] = useState({
    companies: 0,
    associations: 0,
    total_collected: 0,
    total_employees: 0
  });
  const [campaigns, setCampaigns] = useState([]);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    // Redirect if logged in
    if (user) {
      const dashboards = {
        admin: '/admin',
        company: '/company',
        association: '/association',
        ecommerce: '/ecommerce',
        employee: '/employee'
      };
      navigate(dashboards[user.role] || '/');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Fetch all data
    const fetchData = async () => {
      try {
        const [statsRes, campaignsRes, promotionsRes] = await Promise.all([
          fetch(`${API}/public/stats`),
          fetch(`${API}/campaigns/all`),
          fetch(`${API}/promotions/all`)
        ]);
        
        if (statsRes.ok) setStats(await statsRes.json());
        if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
        if (promotionsRes.ok) setPromotions(await promotionsRes.json());
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Demo campaigns data
  const demoCampaigns = [
    {
      campaign_id: 'demo_1',
      title: 'Aide aux enfants défavorisés',
      description: 'Soutenez l\'éducation des enfants dans les quartiers prioritaires. Chaque don compte pour leur avenir.',
      goal_amount: 15000,
      current_amount: 11250,
      image_url: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=400&h=250&fit=crop',
      association_name: 'Association Espoir'
    },
    {
      campaign_id: 'demo_2',
      title: 'Protection de l\'environnement',
      description: 'Participez à la reforestation et à la protection de la biodiversité dans nos régions.',
      goal_amount: 25000,
      current_amount: 18750,
      image_url: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=400&h=250&fit=crop',
      association_name: 'Éco-Planète'
    },
    {
      campaign_id: 'demo_3',
      title: 'Repas solidaires',
      description: 'Offrez des repas chauds aux personnes sans-abri pendant l\'hiver.',
      goal_amount: 8000,
      current_amount: 6400,
      image_url: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?w=400&h=250&fit=crop',
      association_name: 'Main Tendue'
    }
  ];

  // Demo promotions data
  const demoPromotions = [
    {
      promotion_id: 'promo_1',
      title: 'Mode Éthique -30%',
      description: 'Collection printemps de vêtements éco-responsables. Tissus biologiques et production locale.',
      discount_percentage: 30,
      banner_url: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=200&fit=crop',
      company_name: 'GreenWear'
    },
    {
      promotion_id: 'promo_2',
      title: 'Tech Reconditionnée -25%',
      description: 'Smartphones et ordinateurs reconditionnés garantis 2 ans. Économie circulaire.',
      discount_percentage: 25,
      banner_url: 'https://images.unsplash.com/photo-1468495244123-6c6c332eeece?w=400&h=200&fit=crop',
      company_name: 'ReNew Tech'
    },
    {
      promotion_id: 'promo_3',
      title: 'Bio & Local -20%',
      description: 'Panier de produits frais, bio et locaux livré chez vous chaque semaine.',
      discount_percentage: 20,
      banner_url: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=200&fit=crop',
      company_name: 'Terroir Direct'
    }
  ];

  // Helper to validate image URL
  const isValidImageUrl = (url) => {
    if (!url || url.trim() === '') return false;
    if (url.includes('example.com')) return false;
    return url.startsWith('http');
  };

  // Default fallback images
  const defaultCampaignImage = 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=400&h=250&fit=crop';
  const defaultPromoImage = 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=400&h=200&fit=crop';

  // Always display exactly 3 campaigns - use demo data to fill if needed
  const displayCampaigns = (() => {
    // Normalize real campaigns to have required fields
    const normalizedCampaigns = campaigns.map(c => ({
      ...c,
      image_url: isValidImageUrl(c.image_url) ? c.image_url : defaultCampaignImage,
      association_name: c.association_name || 'Association partenaire',
      current_amount: c.current_amount || 0,
      goal_amount: c.goal_amount || 10000
    }));
    
    if (normalizedCampaigns.length >= 3) {
      return normalizedCampaigns.slice(0, 3);
    }
    // Fill with demo data if not enough real campaigns
    const needed = 3 - normalizedCampaigns.length;
    return [...normalizedCampaigns, ...demoCampaigns.slice(0, needed)];
  })();

  // Always display exactly 3 promotions - use demo data to fill if needed
  const displayPromotions = (() => {
    // Normalize real promotions to have required fields
    const normalizedPromotions = promotions.map(p => ({
      ...p,
      banner_url: isValidImageUrl(p.banner_url) ? p.banner_url : defaultPromoImage,
      company_name: p.company_name || 'Partenaire',
      discount_percentage: p.discount_percentage || 20
    }));
    
    if (normalizedPromotions.length >= 3) {
      return normalizedPromotions.slice(0, 3);
    }
    // Fill with demo data if not enough real promotions
    const needed = 3 - normalizedPromotions.length;
    return [...normalizedPromotions, ...demoPromotions.slice(0, needed)];
  })();

  // Investment portfolios data
  const portfolios = [
    {
      name: 'Portefeuille Prudent',
      risk: 'Faible',
      return_ytd: 4.2,
      return_1y: 5.8,
      allocation: [
        { name: 'Obligations', percent: 60, color: 'bg-violet-500' },
        { name: 'Actions', percent: 25, color: 'bg-pink-500' },
        { name: 'Monétaire', percent: 15, color: 'bg-purple-300' }
      ],
      trend: 'up',
      minInvest: 50
    },
    {
      name: 'Portefeuille Équilibré',
      risk: 'Modéré',
      return_ytd: 7.5,
      return_1y: 9.2,
      allocation: [
        { name: 'Actions', percent: 50, color: 'bg-violet-500' },
        { name: 'Obligations', percent: 35, color: 'bg-pink-500' },
        { name: 'Alternatifs', percent: 15, color: 'bg-purple-300' }
      ],
      trend: 'up',
      minInvest: 100
    },
    {
      name: 'Portefeuille Dynamique',
      risk: 'Élevé',
      return_ytd: 12.8,
      return_1y: 15.4,
      allocation: [
        { name: 'Actions', percent: 75, color: 'bg-violet-500' },
        { name: 'Crypto', percent: 15, color: 'bg-pink-500' },
        { name: 'Obligations', percent: 10, color: 'bg-purple-300' }
      ],
      trend: 'up',
      minInvest: 200
    }
  ];

  // Top performing stocks/ETFs
  const topStocks = [
    { symbol: 'MSCI ESG', name: 'Actions Responsables', change: +8.4, price: 142.50 },
    { symbol: 'GREEN ENR', name: 'Énergies Vertes', change: +12.2, price: 87.30 },
    { symbol: 'TECH EU', name: 'Tech Européenne', change: +6.7, price: 234.80 },
    { symbol: 'SANTE+', name: 'Santé Innovation', change: +5.9, price: 178.40 },
    { symbol: 'IMMO DUR', name: 'Immobilier Durable', change: +3.8, price: 95.60 }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/favicon.png" 
              alt="InvesThune Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              InvesThune
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-slate-600 hover:text-slate-800"
              onClick={() => navigate('/login')}
              data-testid="header-login-btn"
            >
              Connexion
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6"
              onClick={() => navigate('/register')}
              data-testid="header-register-btn"
            >
              Commencer
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 grain"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-6">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">Donnez. Investissez. Impactez.</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Votre épargne,<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-500">votre impact</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-lg leading-relaxed">
                InvesThune révolutionne l'épargne solidaire. Alimentez votre porte-monnaie, 
                soutenez des causes qui comptent, et faites fructifier votre argent tout en faisant le bien.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-violet-500/25"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  Créer mon porte-monnaie
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-violet-200 text-violet-700 hover:bg-violet-50 rounded-full px-6 py-6"
                  onClick={() => navigate('/login')}
                >
                  Découvrir
                </Button>
              </div>
            </div>
            <div className="relative animate-slide-up stagger-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=400&fit=crop" 
                  alt="Investissement et donation"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-violet-900/40 to-transparent"></div>
              </div>
              {/* Floating stats card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl animate-fade-in stagger-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800 counter">
                      {stats.total_collected.toLocaleString('fr-FR')}€
                    </p>
                    <p className="text-sm text-slate-500">d'impact généré</p>
                  </div>
                </div>
              </div>
              {/* Second floating card */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-xl animate-fade-in stagger-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                    <Heart className="w-4 h-4 text-pink-600" />
                  </div>
                  <span className="text-sm font-semibold text-slate-700">+{stats.associations} associations</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center animate-slide-up">
              <p className="text-4xl font-bold text-violet-600 counter mb-2">
                {stats.companies}+
              </p>
              <p className="text-slate-600">Entreprises partenaires</p>
            </div>
            <div className="text-center animate-slide-up stagger-1">
              <p className="text-4xl font-bold text-pink-500 counter mb-2">
                {stats.associations}+
              </p>
              <p className="text-slate-600">Associations soutenues</p>
            </div>
            <div className="text-center animate-slide-up stagger-2">
              <p className="text-4xl font-bold text-purple-600 counter mb-2">
                {stats.total_employees}+
              </p>
              <p className="text-slate-600">Membres actifs</p>
            </div>
            <div className="text-center animate-slide-up stagger-3">
              <p className="text-4xl font-bold text-violet-600 counter mb-2">
                {stats.total_collected.toLocaleString('fr-FR')}€
              </p>
              <p className="text-slate-600">Impact total</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CAMPAIGNS / ASSOCIATIONS SECTION ==================== */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-pink-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 px-3 py-1 rounded-full mb-4">
                <Heart className="w-4 h-4" />
                <span className="text-sm font-medium">Projets soutenus</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Campagnes en cours
              </h2>
              <p className="text-lg text-slate-600 mt-2">Découvrez les associations que vous pouvez soutenir</p>
            </div>
            <Button 
              variant="outline" 
              className="hidden md:flex border-pink-200 text-pink-700 hover:bg-pink-50 rounded-full"
              onClick={() => navigate('/register?role=employee')}
            >
              Voir toutes les campagnes
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayCampaigns.map((campaign, index) => {
              const progress = (campaign.current_amount / campaign.goal_amount) * 100;
              return (
                <Card 
                  key={campaign.campaign_id} 
                  className="bg-white border-pink-100 rounded-2xl overflow-hidden card-hover animate-slide-up group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={campaign.image_url || 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=400&h=250&fit=crop'} 
                      alt={campaign.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur text-pink-700 text-sm font-medium rounded-full">
                        {campaign.association_name || 'Association partenaire'}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {campaign.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{campaign.description}</p>
                    
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-500">Collecté</span>
                        <span className="font-bold text-pink-600">
                          {campaign.current_amount.toLocaleString('fr-FR')}€ / {campaign.goal_amount.toLocaleString('fr-FR')}€
                        </span>
                      </div>
                      <div className="h-3 bg-pink-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-pink-500 to-rose-500 rounded-full transition-all duration-1000"
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{progress.toFixed(0)}% de l'objectif atteint</p>
                    </div>

                    <Button 
                      className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-full"
                      onClick={() => navigate('/register?role=employee')}
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Soutenir ce projet
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Impact metrics */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-pink-100 text-center">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Heart className="w-6 h-6 text-pink-600" />
              </div>
              <p className="text-2xl font-bold text-slate-800">{displayCampaigns.length}+</p>
              <p className="text-sm text-slate-500">Projets actifs</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-pink-100 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-slate-800">2,450+</p>
              <p className="text-sm text-slate-500">Donateurs</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-pink-100 text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-violet-600" />
              </div>
              <p className="text-2xl font-bold text-slate-800">89%</p>
              <p className="text-sm text-slate-500">Taux de réussite</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-pink-100 text-center">
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-6 h-6 text-rose-600" />
              </div>
              <p className="text-2xl font-bold text-slate-800">48,000€</p>
              <p className="text-sm text-slate-500">Collectés ce mois</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROMOTIONS E-COMMERCE SECTION ==================== */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-3 py-1 rounded-full mb-4">
                <ShoppingBag className="w-4 h-4" />
                <span className="text-sm font-medium">Offres partenaires</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Promotions exclusives
              </h2>
              <p className="text-lg text-slate-600 mt-2">Économisez avec nos partenaires e-commerce responsables</p>
            </div>
            <Button 
              variant="outline" 
              className="hidden md:flex border-violet-200 text-violet-700 hover:bg-violet-50 rounded-full"
              onClick={() => navigate('/register?role=employee')}
            >
              Voir toutes les offres
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {displayPromotions.map((promo, index) => (
              <Card 
                key={promo.promotion_id} 
                className="bg-white border-violet-100 rounded-2xl overflow-hidden card-hover animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-36 overflow-hidden">
                  <img 
                    src={promo.banner_url || 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=400&h=200&fit=crop'} 
                    alt={promo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-3 right-3">
                    <span className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-lg font-bold rounded-full shadow-lg">
                      -{promo.discount_percentage}%
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2 py-1 bg-white/90 backdrop-blur text-slate-700 text-xs font-medium rounded">
                      {promo.company_name || 'Partenaire'}
                    </span>
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-slate-800 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {promo.title}
                  </h3>
                  <p className="text-slate-500 text-sm mb-3 line-clamp-2">{promo.description}</p>
                  <Button 
                    variant="outline"
                    className="w-full border-violet-200 text-violet-700 hover:bg-violet-50 rounded-full text-sm"
                    onClick={() => navigate('/register?role=employee')}
                  >
                    Profiter de l'offre
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Promo banner */}
          <div className="mt-12 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-500 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Jusqu'à -35% chez nos partenaires
                </h3>
                <p className="text-purple-100">Créez votre compte et accédez à toutes les promotions exclusives</p>
              </div>
              <Button 
                className="bg-white text-violet-700 hover:bg-purple-50 rounded-full px-8 py-6 font-semibold shadow-xl whitespace-nowrap"
                onClick={() => navigate('/register?role=employee')}
              >
                <Gift className="w-5 h-5 mr-2" />
                Accéder aux offres
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== INVESTMENTS SECTION ==================== */}
      <section className="py-20 px-6 bg-gradient-to-b from-purple-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 px-4 py-2 rounded-full mb-4">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">Investissements</span>
              <span className="px-2 py-0.5 bg-violet-600 text-white text-xs font-bold rounded-full">NOUVEAU</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Faites fructifier votre épargne
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Investissez dans des portefeuilles diversifiés et responsables. Votre argent travaille pour vous et pour la planète.
            </p>
          </div>

          {/* Portfolio Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {portfolios.map((portfolio, index) => (
              <Card 
                key={portfolio.name} 
                className="bg-white border-purple-100 rounded-2xl overflow-hidden card-hover animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {portfolio.name}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        portfolio.risk === 'Faible' ? 'bg-green-100 text-green-700' :
                        portfolio.risk === 'Modéré' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        Risque {portfolio.risk}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-green-600">
                        <ArrowUpRight className="w-4 h-4" />
                        <span className="font-bold">+{portfolio.return_ytd}%</span>
                      </div>
                      <span className="text-xs text-slate-400">YTD</span>
                    </div>
                  </div>

                  {/* Allocation bar */}
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-2">Allocation</p>
                    <div className="h-3 rounded-full overflow-hidden flex">
                      {portfolio.allocation.map((alloc, i) => (
                        <div 
                          key={i}
                          className={`${alloc.color} h-full`}
                          style={{ width: `${alloc.percent}%` }}
                          title={`${alloc.name}: ${alloc.percent}%`}
                        ></div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2">
                      {portfolio.allocation.map((alloc, i) => (
                        <span key={i} className="text-xs text-slate-500">
                          <span className={`inline-block w-2 h-2 rounded-full ${alloc.color} mr-1`}></span>
                          {alloc.name} {alloc.percent}%
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3 border-t border-purple-50">
                    <div>
                      <p className="text-xs text-slate-400">Rendement 1 an</p>
                      <p className="font-bold text-violet-600">+{portfolio.return_1y}%</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Min. invest.</p>
                      <p className="font-bold text-slate-800">{portfolio.minInvest}€</p>
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-full"
                    onClick={() => navigate('/register?role=employee')}
                  >
                    Investir maintenant
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Market Overview Table */}
          <Card className="bg-white border-purple-100 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-purple-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Top performances
                  </h3>
                  <p className="text-sm text-slate-500">Les ETFs et fonds les plus performants du moment</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock className="w-4 h-4" />
                  Mis à jour il y a 5 min
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-purple-50/50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Symbole</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Nom</th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-600">Prix</th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-600">Variation</th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-slate-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {topStocks.map((stock, index) => (
                    <tr key={stock.symbol} className="border-b border-purple-50 hover:bg-purple-50/30 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' :
                            index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400' :
                            'bg-gradient-to-br from-violet-500 to-purple-600'
                          }`}>
                            <span className="text-white text-xs font-bold">{stock.symbol.substring(0, 2)}</span>
                          </div>
                          <span className="font-mono font-bold text-slate-800">{stock.symbol}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-slate-600">{stock.name}</td>
                      <td className="py-4 px-6 text-right font-bold text-slate-800">{stock.price.toFixed(2)}€</td>
                      <td className="py-4 px-6 text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold ${
                          stock.change >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {stock.change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {stock.change >= 0 ? '+' : ''}{stock.change}%
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-violet-200 text-violet-700 hover:bg-violet-50 rounded-full"
                          onClick={() => navigate('/register?role=employee')}
                        >
                          Investir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 bg-purple-50/30 border-t border-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-1">
                    <Shield className="w-4 h-4 text-violet-600" />
                    <span>Investissements sécurisés</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-violet-600" />
                    <span>Exécution instantanée</span>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  className="text-violet-600 hover:text-violet-700"
                  onClick={() => navigate('/register?role=employee')}
                >
                  Voir tous les actifs
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Disclaimer */}
          <p className="text-center text-xs text-slate-400 mt-6">
            Les performances passées ne préjugent pas des performances futures. Investir comporte des risques.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Une plateforme, trois pouvoirs
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Donnez du sens à votre argent avec des outils simples et puissants
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-purple-100 rounded-2xl p-8 card-hover animate-slide-up group">
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Heart className="w-7 h-7 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Donnez
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Soutenez les associations de votre choix. Chaque don compte et transforme des vies.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-violet-100 rounded-2xl p-8 card-hover animate-slide-up stagger-1 group">
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <LineChart className="w-7 h-7 text-violet-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Investissez
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Faites fructifier votre épargne dans des portefeuilles responsables. Votre argent grandit et fait le bien.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-purple-100 rounded-2xl p-8 card-hover animate-slide-up stagger-2 group">
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Gift className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Économisez
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Profitez des promotions exclusives de nos partenaires e-commerce. Consommez malin.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* For Who Section - Bento Grid */}
      <section className="py-20 px-6 bg-gradient-to-b from-purple-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Rejoignez l'écosystème
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              InvesThune connecte particuliers, entreprises, associations et e-commerces
            </p>
          </div>

          <div className="bento-grid">
            {/* Particuliers/Salariés - Large card */}
            <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-100 rounded-2xl p-8 bento-large card-hover animate-slide-up">
              <CardContent className="p-0 h-full flex flex-col">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                    <Wallet className="w-8 h-8 text-white" />
                  </div>
                  <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">Nouveau</span>
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Particuliers & Salariés
                </h3>
                <p className="text-slate-600 mb-6 flex-grow">
                  Votre porte-monnaie solidaire pour donner, investir et économiser. 
                  Transformez chaque euro en impact positif.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-700">
                    <ChevronRight className="w-4 h-4 text-violet-600" />
                    Porte-monnaie avec alimentations libres
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <ChevronRight className="w-4 h-4 text-violet-600" />
                    Dons aux associations partenaires
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <ChevronRight className="w-4 h-4 text-violet-600" />
                    Investissements responsables
                  </li>
                </ul>
                <Button 
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full w-fit shadow-lg shadow-violet-500/25"
                  onClick={() => navigate('/register?role=employee')}
                  data-testid="particulier-cta-btn"
                >
                  Créer mon compte
                </Button>
              </CardContent>
            </Card>

            {/* Associations */}
            <Card className="bg-gradient-to-br from-pink-50 to-white border-pink-100 rounded-2xl p-8 card-hover animate-slide-up stagger-1">
              <CardContent className="p-0 h-full flex flex-col">
                <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-pink-500/20">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Associations
                </h3>
                <p className="text-slate-600 mb-6 flex-grow">
                  Lancez vos campagnes de collecte et recevez des dons récurrents.
                </p>
                <Button 
                  variant="outline"
                  className="border-pink-200 text-pink-700 hover:bg-pink-50 rounded-full w-fit"
                  onClick={() => navigate('/register?role=association')}
                  data-testid="association-cta-btn"
                >
                  Lancer une campagne
                </Button>
              </CardContent>
            </Card>

            {/* Entreprises */}
            <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100 rounded-2xl p-8 card-hover animate-slide-up stagger-2">
              <CardContent className="p-0 h-full flex flex-col">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/20">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Entreprises
                </h3>
                <p className="text-slate-600 mb-6 flex-grow">
                  Engagez vos salariés dans une démarche RSE innovante et mesurable.
                </p>
                <Button 
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-full w-fit"
                  onClick={() => navigate('/register?role=company')}
                  data-testid="entreprise-cta-btn"
                >
                  Devenir partenaire
                </Button>
              </CardContent>
            </Card>

            {/* E-commerce */}
            <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200 rounded-2xl p-6 card-hover animate-slide-up stagger-3">
              <CardContent className="p-0 h-full flex flex-col">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-4">
                  <ShoppingBag className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  E-commerce
                </h3>
                <p className="text-slate-600 text-sm mb-4 flex-grow">
                  Proposez vos offres exclusives à notre communauté engagée.
                </p>
                <Button 
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full w-fit text-sm"
                  onClick={() => navigate('/register?role=ecommerce')}
                  data-testid="ecommerce-cta-btn"
                >
                  Rejoindre
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-violet-600 via-purple-600 to-pink-500 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-400/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Prêt à donner du sens à votre argent ?
          </h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">
            Rejoignez des milliers de personnes qui transforment leur épargne en impact positif. 
            Inscription gratuite, sans engagement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              className="bg-white text-violet-700 hover:bg-purple-50 rounded-full px-8 py-6 text-lg font-semibold shadow-xl"
              onClick={() => navigate('/register')}
              data-testid="footer-cta-btn"
            >
              Commencer gratuitement
            </Button>
            <Button 
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6"
              onClick={() => navigate('/login')}
            >
              J'ai déjà un compte
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/favicon.png" 
                alt="InvesThune Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                InvesThune
              </span>
            </div>
            <p className="text-slate-400 text-sm">
              © 2026 InvesThune. Donnez. Investissez. Impactez.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
